import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  setSelectedEcommerce,
  setSelectedGroup
} from '@/redux/features/general-data/general-data-slice'
import {
  captureEcommerceScopeChange,
  toEcommerceScope
} from '@/infrastructure/posthog'
import { useIsReverStaff } from './use-is-rever-staff'

export function useSelectEcommerce() {
  const dispatch = useAppDispatch()
  const capture = useCaptureScopeChange()

  return useCallback(
    (ecommerceId: string | undefined) => {
      if (!capture(ecommerceId, undefined)) return
      dispatch(setSelectedEcommerce(ecommerceId))
    },
    [dispatch, capture]
  )
}

export function useSelectGroup() {
  const dispatch = useAppDispatch()
  const capture = useCaptureScopeChange()

  return useCallback(
    (groupId: string | undefined) => {
      if (!capture(undefined, groupId)) return
      dispatch(setSelectedGroup(groupId))
    },
    [dispatch, capture]
  )
}

// Reports the switch and says whether there was one, so a repeated pick dispatches nothing.
function useCaptureScopeChange() {
  const selectedEcommerce = useAppSelector(
    (s) => s.generalData.selectedEcommerce
  )
  const selectedGroup = useAppSelector((s) => s.generalData.selectedGroup)
  const ecommerceCount = useAppSelector(
    (s) => s.userApi.getMe.response.user?.e_commerces_ids?.length ?? 0
  )
  // Tagged, not dropped: our own dogfooding stays visible but filterable.
  const isReverUser = useIsReverStaff()

  return useCallback(
    (ecommerceId: string | undefined, groupId: string | undefined) => {
      const currentEcommerce = selectedEcommerce || undefined
      const currentGroup = selectedGroup || undefined
      if (ecommerceId === currentEcommerce && groupId === currentGroup)
        return false

      captureEcommerceScopeChange({
        ecommerceScope: toEcommerceScope(ecommerceId, groupId),
        fromEcommerceScope: toEcommerceScope(currentEcommerce, currentGroup),
        ecommerceId,
        groupId,
        ecommerceCount,
        isReverUser
      })
      return true
    },
    [selectedEcommerce, selectedGroup, ecommerceCount, isReverUser]
  )
}
